import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { Group } from '@domain/types';

import {
  createGroup,
  deleteGroup,
  listGroupSummaries,
  listGroups,
  updateGroup,
  type GroupInput,
  type GroupSummary,
} from './repository';

type GroupsState = {
  groups: Group[];
  summaries: Record<string, GroupSummary>;
  activeGroupId: string | null;
  loading: boolean;
  error: string | null;
  load: () => Promise<void>;
  /** Re-reads just the roll-up counts, without disturbing the selection. */
  refreshSummaries: () => Promise<void>;
  setActiveGroup: (id: string | null) => void;
  create: (input: GroupInput) => Promise<Group>;
  update: (id: string, patch: Partial<GroupInput & { archived: boolean }>) => Promise<void>;
  remove: (id: string) => Promise<void>;
};

export const useGroupsStore = create<GroupsState>()(
  persist(
    (set, get) => ({
      groups: [],
      summaries: {},
      activeGroupId: null,
      loading: false,
      error: null,

      load: async () => {
        set({ loading: true, error: null });
        try {
          const [groups, summaryList] = await Promise.all([
            listGroups(true),
            listGroupSummaries(),
          ]);

          const summaries = Object.fromEntries(
            summaryList.map((summary) => [summary.groupId, summary])
          );

          // Keep the selection valid: fall back to the first unarchived group.
          const { activeGroupId } = get();
          const stillExists = groups.some((group) => group.id === activeGroupId);
          const fallback = groups.find((group) => !group.archived)?.id ?? null;

          set({
            groups,
            summaries,
            activeGroupId: stillExists ? activeGroupId : fallback,
            loading: false,
          });
        } catch (error) {
          set({ loading: false, error: (error as Error).message });
        }
      },

      refreshSummaries: async () => {
        try {
          const summaryList = await listGroupSummaries();
          set({
            summaries: Object.fromEntries(
              summaryList.map((summary) => [summary.groupId, summary])
            ),
          });
        } catch (error) {
          set({ error: (error as Error).message });
        }
      },

      setActiveGroup: (activeGroupId) => set({ activeGroupId }),

      create: async (input) => {
        const group = await createGroup(input);
        await get().load();
        set({ activeGroupId: group.id });
        return group;
      },

      update: async (id, patch) => {
        await updateGroup(id, patch);
        await get().load();
      },

      remove: async (id) => {
        await deleteGroup(id);
        if (get().activeGroupId === id) set({ activeGroupId: null });
        await get().load();
      },
    }),
    {
      name: 'gem.groups',
      // Only the selection is worth persisting; rows always come from the API.
      // localStorage is synchronous, so this is restored before first render.
      partialize: ({ activeGroupId }) => ({ activeGroupId }),
    }
  )
);

export function useActiveGroup(): Group | null {
  const groups = useGroupsStore((state) => state.groups);
  const activeGroupId = useGroupsStore((state) => state.activeGroupId);
  return groups.find((group) => group.id === activeGroupId) ?? null;
}

export function useActiveCurrency(): string {
  return useActiveGroup()?.currency ?? 'USD';
}
